/**
 * UserLoginPage
 * [UC-036] User login with email/password and Google OAuth
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';

interface LoginForm {
  email: string;
  password: string;
}

export function UserLoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const [form, setForm] = useState<LoginForm>({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (field: keyof LoginForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const handleTraditionalLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.email || !form.password) {
      setErrorMessage(t('userLogin.validation.emailPasswordRequired', 'Email and password are required'));
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await auth.login(form.email, form.password);
      navigate('/user-dashboard');
    } catch (error: any) {
      console.error('Login failed:', error);
      const status = error.response?.status;
      switch (status) {
        case 400:
        case 401:
          setErrorMessage(t('userLogin.errors.invalidCredentials', 'Invalid email or password'));
          break;
        case 403:
          setErrorMessage(t('userLogin.errors.accountDeactivated', 'Account has been deactivated'));
          break;
        default:
          setErrorMessage(t('userLogin.errors.loginFailed', 'Login failed. Please try again.'));
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-ground)] flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <img
            src="/logo.svg"
            alt="POLYREQ"
            className="mx-auto h-16 w-16 sm:h-20 sm:w-20 object-contain mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)]">
            {t('userLogin.title', 'Welcome back')}
          </h1>
          <p className="mt-2 text-sm text-[var(--text-muted)]">
            {t('userLogin.subtitle', 'Sign in to POLYREQ')}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] p-4 sm:p-6">
          <form onSubmit={handleTraditionalLogin} className="space-y-4">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
                {t('userLogin.fields.email', 'Email')}
              </label>
              <input
                type="email"
                placeholder={t('userLogin.placeholders.email', 'you@example.com')}
                value={form.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2.5 sm:py-2 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-[var(--text-secondary)]">
                  {t('userLogin.fields.password', 'Password')}
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/user-password-reset-request')}
                  className="text-xs text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] transition-colors"
                >
                  {t('userLogin.links.forgotPassword', 'Forgot password?')}
                </button>
              </div>
              <input
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-3 py-2.5 sm:py-2 bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-600/15 border border-red-600/25 text-red-700 px-4 py-3 rounded-lg text-sm">
                {errorMessage}
              </div>
            )}

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isSubmitting || !form.email || !form.password}
              className="w-full mt-6 px-4 py-2.5 bg-[var(--accent-primary)] text-white rounded-lg hover:bg-[var(--accent-secondary)] disabled:bg-[var(--surface-overlay)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {isSubmitting ? t('userLogin.buttons.signingIn', 'Signing in...') : t('userLogin.buttons.signIn', 'Sign In')}
            </button>
          </form>
        </div>

        {/* Registration Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-[var(--text-muted)]">
            {t('userLogin.registration.noAccount', "Don't have an account?")}{' '}
            <button
              onClick={() => navigate('/user-registration')}
              className="text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] transition-colors font-medium"
            >
              {t('userLogin.registration.signUp', 'Sign up')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default UserLoginPage;
